import { useEffect, useRef, useState } from "react";
import { overlayClose } from "../../../shared/utils/overlayClose";

/**
 * adminListaUI — piezas compartidas de las pantallas de lista del Portal Admin
 * (Reservaciones, Avisos de Pago…).
 *
 * Las dos pantallas tienen el mismo armazón: tarjetas de resumen con barra de
 * avance, pestañas por estado con contador, barra de búsqueda con desplegables
 * de filtros, orden y paginación. Estaba duplicado línea por línea, así que un
 * ajuste visual había que hacerlo dos veces y era cuestión de tiempo que se
 * desincronizaran. Acá vive una sola copia.
 */

// ── Formato de fechas ────────────────────────────────────────────────────────

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/**
 * "2026-12-19" → Date local. `new Date("2026-12-19")` la interpreta como UTC y
 * en Costa Rica (UTC-6) retrocede un día: la fecha del 19 se mostraba 18.
 */
export function fechaLocal(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function fechaLarga(iso) {
  const f = fechaLocal(iso);
  return f ? `${f.getDate()} ${MESES_CORTOS[f.getMonth()]} ${f.getFullYear()}` : "-";
}

/** Días calendario entre dos fechas (positivo = `hasta` es posterior). */
export function diasEntre(desde, hasta) {
  const a = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate());
  const b = new Date(hasta.getFullYear(), hasta.getMonth(), hasta.getDate());
  return Math.round((b - a) / 86400000);
}

export function hace(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return "-";
  const dias = diasEntre(d, new Date());
  if (dias <= 0) return "Hoy";
  if (dias === 1) return "1 día";
  if (dias < 30) return `${dias} días`;
  const meses = Math.floor(dias / 30);
  return meses === 1 ? "1 mes" : `${meses} meses`;
}

/** "hoy" o "hace 3 días". Concatenar "hace " a `hace()` daba "hace Hoy". */
export function desdeHace(iso) {
  const h = hace(iso);
  if (h === "-") return h;
  return h === "Hoy" ? "hoy" : `hace ${h}`;
}

// ── Estilos sueltos ──────────────────────────────────────────────────────────

export const labelSt = { fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 };
export const fldStyle = (extra = {}) => ({
  width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8,
  border: "1px solid var(--border-card)", background: "var(--bg-input, var(--hover-bg))",
  color: "var(--text-primary)", fontSize: 13, outline: "none", ...extra,
});
/** Dato suelto de una fila (ícono + texto) y encabezado de columna. */
export const metaSt = { fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 5, minWidth: 0 };
export const colLabelSt = { fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 };

// ── Componentes ──────────────────────────────────────────────────────────────

/** Pastilla de estado: `cfg` = { bg, border, text, icon, label }. */
export function EstadoBadge({ cfg }) {
  return (
    <span style={{
      background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.text,
      borderRadius: 20, padding: "3px 10px", fontSize: 10.5, fontWeight: 800,
      display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
      letterSpacing: 0.3,
    }}>
      <i className={`fa-solid ${cfg.icon}`} style={{ fontSize: 8.5 }} />
      {cfg.label}
    </span>
  );
}

/**
 * Tarjeta de resumen con barra de avance: el número dice cuántos hay, la barra
 * qué tajada del total representan — que es lo que se quiere saber de un
 * vistazo (¿cuánto de lo que hay está sin revisar?).
 */
export function StatCard({ icon, label, value, total, color, mostrarPorcentaje = true }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 14, padding: "16px 18px 14px", position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `radial-gradient(circle at 92% -10%, ${color}22, transparent 62%)`,
      }} />
      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 13 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11, flexShrink: 0,
          background: `${color}1f`, border: `1px solid ${color}3d`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color, fontSize: 16,
        }}>
          <i className={`fa-solid ${icon}`} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 4, fontWeight: 500,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
        </div>
      </div>
      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
        <div style={{ flex: 1, height: 4, borderRadius: 99, background: "var(--hover-bg)", overflow: "hidden" }}>
          <div style={{
            width: `${mostrarPorcentaje ? pct : 100}%`, height: "100%", borderRadius: 99,
            background: color, opacity: mostrarPorcentaje ? 1 : 0.35, transition: "width .3s",
          }} />
        </div>
        {mostrarPorcentaje && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", minWidth: 30, textAlign: "right" }}>
            {pct}%
          </span>
        )}
      </div>
    </div>
  );
}

/** Pestañas por estado con su contador. `opciones` = [{ key, label, color }]. */
export function PestanasEstado({ opciones, valor, conteos, onChange }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
      {opciones.map(o => {
        const activo = valor === o.key;
        return (
          <button key={o.key} onClick={() => onChange(o.key)} style={{
            padding: "8px 16px", borderRadius: 22, cursor: "pointer",
            fontWeight: 700, fontSize: 13,
            border: `1px solid ${activo ? "transparent" : "var(--border-card)"}`,
            background: activo ? "linear-gradient(135deg,#f97316,#fb923c)" : "var(--bg-card)",
            color: activo ? "#fff" : "var(--text-secondary)",
            display: "flex", alignItems: "center", gap: 8,
            boxShadow: activo ? "0 3px 12px rgba(249,115,22,0.3)" : "none",
            transition: "all .15s",
          }}>
            {o.label}
            <span style={{
              borderRadius: 99, padding: "1px 7px", fontSize: 10.5, fontWeight: 800,
              background: activo ? "rgba(255,255,255,0.25)" : "var(--hover-bg)",
              color: activo ? "#fff" : o.color,
            }}>
              {conteos[o.key] ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Botón que despliega un panel debajo y se cierra al tocar afuera. */
export function Desplegable({ icono, etiqueta, activos = 0, ancho = 300, children }) {
  const [abierto, setAbierto] = useState(false);
  const [dx, setDx] = useState(0);     // corrección para no salirse de la pantalla
  const ref = useRef(null);

  // El panel cuelga del botón alineado a la derecha. En pantallas angostas los
  // controles se reacomodan y el botón puede quedar cerca del borde izquierdo:
  // ahí un panel de 380px anclado a la derecha se salía de la ventana (medido:
  // left -171 en un viewport de 420). Se mide el ancla y se corre lo justo para
  // que entre, con 12px de aire a cada lado.
  const anchoReal = typeof window !== "undefined" ? Math.min(ancho, window.innerWidth - 24) : ancho;
  useEffect(() => {
    if (!abierto) return;
    function ajustar() {
      const ancla = ref.current?.getBoundingClientRect();
      if (!ancla) return;
      const w = Math.min(ancho, window.innerWidth - 24);
      const izqSinCorregir = ancla.right - w;
      let izq = izqSinCorregir;
      if (izq < 12) izq = 12;
      if (izq + w > window.innerWidth - 12) izq = window.innerWidth - 12 - w;
      setDx(Math.round(izq - izqSinCorregir));
    }
    ajustar();
    window.addEventListener("resize", ajustar);
    return () => window.removeEventListener("resize", ajustar);
  }, [abierto, ancho]);

  useEffect(() => {
    if (!abierto) return;
    function fuera(e) {
      if (!ref.current || ref.current.contains(e.target)) return;
      // Los desplegables de SearchSelect se pintan en un portal colgado del
      // body: están "afuera" del panel en el DOM pero son parte de él. Sin
      // esta excepción, al tocar una opción se cerraba el panel antes de que
      // el click llegara y nunca se podía elegir filial.
      if (e.target.closest?.("[data-portal-dropdown]")) return;
      setAbierto(false);
    }
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setAbierto(a => !a)} style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        padding: "10px 14px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600,
        background: activos ? "rgba(249,115,22,0.08)" : "var(--hover-bg)",
        border: `1px solid ${activos ? "rgba(249,115,22,0.35)" : "var(--border-card)"}`,
        color: activos ? "#f97316" : "var(--text-secondary)", whiteSpace: "nowrap",
      }}>
        <i className={`fa-solid ${icono}`} style={{ fontSize: 12 }} />
        <span style={{ flex: 1, textAlign: "left" }}>{etiqueta}</span>
        {activos > 0 && (
          <span style={{ background: "#f97316", color: "#fff", borderRadius: 99, padding: "0 6px", fontSize: 10, fontWeight: 800 }}>
            {activos}
          </span>
        )}
        <i className={`fa-solid fa-chevron-${abierto ? "up" : "down"}`} style={{ fontSize: 10, opacity: 0.7 }} />
      </button>
      {abierto && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 40,
          width: anchoReal, transform: `translateX(${dx}px)`, padding: "14px 16px",
          background: "var(--bg-card)", border: "1px solid var(--border-card)",
          borderRadius: 12, boxShadow: "0 18px 44px rgba(0,0,0,0.42)",
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

/** Rango de fechas para el desplegable del calendario. */
export function RangoFechas({ desde, hasta, onDesde, onHasta, onLimpiar, titulo = "Fecha" }) {
  return (
    <>
      <p style={{ ...labelSt, marginBottom: 8 }}>{titulo}</p>
      {/* Un input[type=date] nativo no se encoge por debajo de su ancho mínimo
          (~145px), así que con columnas 1fr de 124px se salía del panel.
          `auto-fit` les da 150px o los apila, y el minWidth:0 corta por dentro
          en vez de desbordar. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <label style={labelSt}>Desde</label>
          <input type="date" value={desde} onChange={e => onDesde(e.target.value)} style={fldStyle({ minWidth: 0 })} />
        </div>
        <div style={{ minWidth: 0 }}>
          <label style={labelSt}>Hasta</label>
          <input type="date" value={hasta} onChange={e => onHasta(e.target.value)} style={fldStyle({ minWidth: 0 })} />
        </div>
      </div>
      {(desde || hasta) && (
        <button onClick={onLimpiar} style={{
          marginTop: 12, width: "100%", padding: "8px 12px", borderRadius: 8, cursor: "pointer",
          background: "transparent", border: "1px solid var(--border-card)",
          color: "var(--text-secondary)", fontSize: 12, fontWeight: 600,
        }}>
          Quitar fechas
        </button>
      )}
    </>
  );
}

/** Buscador de la barra de herramientas. */
export function Buscador({ valor, onChange, placeholder }) {
  return (
    <div style={{ position: "relative", minWidth: 0 }}>
      <i className="fa-solid fa-magnifying-glass" style={{
        position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
        color: "var(--text-muted)", fontSize: 12, pointerEvents: "none",
      }} />
      <input
        value={valor}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={fldStyle({ paddingLeft: 38, paddingTop: 10, paddingBottom: 10, borderRadius: 10 })}
      />
      {valor && (
        <button onClick={() => onChange("")} title="Limpiar" style={{
          position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
          background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 12,
        }}>
          <i className="fa-solid fa-xmark" />
        </button>
      )}
    </div>
  );
}

/** Conmutador lista / tarjetas. */
export function SelectorVista({ vista, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, padding: 4, borderRadius: 10,
      background: "var(--hover-bg)", border: "1px solid var(--border-card)" }}>
      {[["lista", "fa-list"], ["tarjetas", "fa-table-cells-large"]].map(([v, ic]) => (
        <button key={v} onClick={() => onChange(v)} title={v === "lista" ? "Ver en lista" : "Ver en tarjetas"}
          style={{
            width: 34, height: 30, borderRadius: 7, cursor: "pointer",
            background: vista === v ? "var(--bg-card)" : "transparent",
            border: `1px solid ${vista === v ? "rgba(249,115,22,0.4)" : "transparent"}`,
            color: vista === v ? "#f97316" : "var(--text-muted)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
          <i className={`fa-solid ${ic}`} style={{ fontSize: 12 }} />
        </button>
      ))}
    </div>
  );
}

/** Barra de "N resultados" + orden, entre la búsqueda y la lista. */
export function BarraResultados({ texto, hayFiltros, onLimpiar, orden, onOrden, opcionesOrden }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
      padding: "11px 18px", borderBottom: "1px solid var(--border-card)", flexWrap: "wrap",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{texto}</span>
        {hayFiltros && (
          <button onClick={onLimpiar} style={{
            background: "none", border: "none", cursor: "pointer", padding: 0,
            color: "#f97316", fontSize: 12, fontWeight: 600,
          }}>
            <i className="fa-solid fa-rotate-left" style={{ marginRight: 5, fontSize: 10 }} />
            Limpiar todo
          </button>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>Ordenar por:</span>
        <select value={orden} onChange={e => onOrden(e.target.value)} style={fldStyle({
          width: "auto", cursor: "pointer", padding: "7px 10px", fontSize: 12.5,
        })}>
          {opcionesOrden.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    </div>
  );
}

export function Pagination({ page, total, pageSize, onChange }) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;
  const btn = (disabled, onClick, children) => (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-card)",
      background: "var(--bg-card)", color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
      cursor: disabled ? "default" : "pointer", fontSize: 13, opacity: disabled ? 0.4 : 1,
    }}>
      {children}
    </button>
  );
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "12px 16px", borderTop: "1px solid var(--border-card)", flexWrap: "wrap", gap: 10,
    }}>
      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
        {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} de {total}
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {btn(page === 1, () => onChange(page - 1), <i className="fa-solid fa-chevron-left" />)}
        <span style={{ padding: "5px 12px", borderRadius: 8, fontSize: 13, background: "#f97316", color: "#fff", fontWeight: 700 }}>
          {page} / {pages}
        </span>
        {btn(page === pages, () => onChange(page + 1), <i className="fa-solid fa-chevron-right" />)}
      </div>
    </div>
  );
}

/** Estado vacío: sin datos del todo, o sin resultados para los filtros. */
export function Vacio({ icono, titulo, detalle }) {
  return (
    <div style={{ padding: "48px 24px", textAlign: "center" }}>
      <div style={{
        width: 60, height: 60, borderRadius: "50%",
        background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 16px", color: "#f97316", fontSize: 24,
      }}>
        <i className={`fa-solid ${icono}`} />
      </div>
      <p style={{ margin: 0, fontWeight: 700, color: "var(--text-primary)", fontSize: 15 }}>{titulo}</p>
      <p style={{ margin: "6px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>{detalle}</p>
    </div>
  );
}

export function Cargando({ texto }) {
  return (
    <div style={{ padding: 48, textAlign: "center", color: "var(--text-secondary)" }}>
      <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: 24, marginBottom: 12, display: "block" }} />
      {texto}
    </div>
  );
}

// Definido a nivel de módulo (no adentro de la página): si va adentro de un
// componente, React lo recrea en cada render y trata al modal como un
// componente distinto en cada tecleo — lo desmonta y remonta, y el input de
// texto pierde el foco después de cada letra.
export function ModalOverlay({ zIndex = 1000, width = 500, onClose, children }) {
  return (
    <div {...overlayClose(onClose)} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(4px)", zIndex,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 16, width: `min(${width}px, 96vw)`,
        boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        maxHeight: "92vh", overflowY: "auto",
      }}>
        {children}
      </div>
    </div>
  );
}

/** Hoja de estilos compartida por las listas del Portal Admin. */
export const ESTILOS_LISTA = `
  .pla-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:20px; }
  .pla-acciones { display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }
  /* Buscador elástico + los controles agrupados a la derecha. En grid de varias
     columnas, al angostarse, "Filtros" se estiraba a todo el ancho de su celda
     y la barra quedaba desbalanceada. */
  .pla-toolbar { display:grid; grid-template-columns:1fr auto; gap:10px; align-items:center; }
  .pla-tools { display:flex; gap:10px; align-items:center; justify-content:flex-end; flex-wrap:wrap; }
  .pla-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(290px,1fr)); gap:14px; padding:16px 18px; }
  @media (max-width:1080px){
    .pla-stats { grid-template-columns:repeat(2,1fr); }
  }
  @media (max-width:900px){
    .pla-acciones { justify-content:flex-start; }
  }
  @media (max-width:620px){
    .pla-stats { grid-template-columns:1fr; }
    .pla-toolbar { grid-template-columns:1fr; }
    .pla-tools { justify-content:flex-start; }
    .pla-tools > div:not(:last-child) { flex:1 1 150px; }
    .pla-acciones button, .pla-acciones a { flex:1 1 auto; justify-content:center; }
  }
`;
